/**
 * V14g Patient record (frozen prototype "patient record" view), as a pure
 * projection over reads the doctor consult already admits: the saved case
 * sheets (current + earlier finalized visits), their today-work facts,
 * consents and evidence, the doctor-lens appointments and the confirmed
 * clinical profile. Nothing here reads, writes or widens access; it only
 * arranges saved facts. Missing facts stay missing ("Not recorded").
 *
 * Role presentation (prototype line 2118): a dental doctor sees Dental +
 * Timeline; any other doctor sees Dental, Skin and Hair + Timeline. A visit
 * belongs to a specialty by its saved category token; a visit without one is
 * listed under Dental and the page says so.
 */
import type {
  CaseSheetClinicalView, CaseSheetConsentsWire, CaseHistoryEntry, ClinicalEvidenceItem,
  ClinicalTodayWorkView, PatientClinicalProfileView,
} from '@/dashboard/api';
import type { Patient360AppointmentWire } from '@/dashboard/patients/patient-360-view';
import { appointmentStatusLabel } from '@/dashboard/patients/patient-360-view';
import { planItemStatusLabel } from '@/dashboard/clinical-workbench/case-sheet-labels';
import { evidenceClassLabel } from './clinical-evidence-view';
import { latestTodayFacts } from './clinical-today-work-view';
import { consentStateLabel } from './consult-consent-view';
import { formatIstShortDate, istCalendarDayYmd } from '@/lib/datetime-ist';

export type RecordSpecialty = 'Dental' | 'Skin' | 'Hair';
export type RecordTab = RecordSpecialty | 'Timeline';
export const RECORD_CARDS = ['Visits', 'Findings', 'Treatment plans', 'Treatments done', 'Medicines',
  'X-rays & reports', 'Consents & documents', 'Appointments'] as const;
export type RecordCard = (typeof RECORD_CARDS)[number];

export interface RecordRow {
  readonly key: string;
  readonly at: string | null;
  readonly date: string;
  readonly main: string;
  readonly side: string;
  readonly open?: { readonly item: ClinicalEvidenceItem };
}

const DENTAL_SPECIALTY = /(^|\s)(dental|dentist|dentistry)(\s|$)|dont|oral|implant|maxillofacial/;

/** The viewer's tabs. An unrecorded specialty is the narrower dental view. */
export function recordSpecialties(viewerSpecialty: string | null | undefined): readonly RecordSpecialty[] {
  const token = (viewerSpecialty ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  return token === '' || DENTAL_SPECIALTY.test(token) ? ['Dental'] : ['Dental', 'Skin', 'Hair'];
}

/** A saved visit's specialty from its category token; none recorded = Dental. */
export function sheetSpecialty(sheet: Pick<CaseSheetClinicalView, 'diagnosisCategory' | 'chiefComplaintCategory'>): { specialty: RecordSpecialty; recorded: boolean } {
  const token = (sheet.diagnosisCategory ?? sheet.chiefComplaintCategory ?? '').trim().toLowerCase();
  if (token === 'skin') return { specialty: 'Skin', recorded: true };
  if (token === 'hair') return { specialty: 'Hair', recorded: true };
  return { specialty: 'Dental', recorded: token !== '' };
}

/** "12 Sep", "14 Mar 2025" or "Today" in clinic time. */
export function recordDate(iso: string | null | undefined, today: string): string {
  if (!iso) return 'Date not recorded';
  const ms = Date.parse(iso.length === 10 ? `${iso}T12:00:00+05:30` : iso);
  if (!Number.isFinite(ms)) return 'Date not recorded';
  const day = istCalendarDayYmd(ms);
  if (day === today) return 'Today';
  return `${formatIstShortDate(day)}${day.slice(0, 4) !== today.slice(0, 4) ? ` ${day.slice(0, 4)}` : ''}`;
}

export interface RecordInput {
  readonly today: string;
  readonly viewerSpecialty: string | null | undefined;
  /** Saved sheets only: the current visit's saved sheet and earlier finalized visits. */
  readonly sheets: readonly CaseSheetClinicalView[];
  readonly history: readonly CaseHistoryEntry[];
  readonly doctorName: (id: number | null | undefined) => string | null;
  readonly effectiveStatus?: (owningSheetId: number, itemId: number) => string | null;
  readonly todayWork: Readonly<Record<number, ClinicalTodayWorkView | null>>;
  readonly consents: Readonly<Record<number, CaseSheetConsentsWire | null>>;
  readonly evidence: Readonly<Record<number, readonly ClinicalEvidenceItem[] | null>>;
  readonly appointments: readonly Patient360AppointmentWire[] | null;
  readonly profile: PatientClinicalProfileView | null;
  readonly returnLine: string | null;
}

export interface PatientRecordModel {
  readonly specialties: readonly RecordSpecialty[];
  readonly tabs: readonly RecordTab[];
  readonly cards: Readonly<Record<RecordSpecialty, Partial<Record<RecordCard, readonly RecordRow[]>>>>;
  readonly timeline: readonly (RecordRow & { readonly specialty: RecordSpecialty; readonly card: RecordCard })[];
  readonly unrecordedArea: boolean;
  readonly tiles: {
    readonly allergies: { readonly text: string; readonly alert: boolean };
    readonly activePlans: string;
    readonly lastVisit: string;
    readonly returnVisit: string;
    readonly historyConfirmed: string;
  };
}

const teeth = (item: CaseSheetClinicalView['planItems'][number]) =>
  item.toothRefs?.map(ref => ref.toothCode).join(', ') || item.toothArea || '';
const named = (place: string, what: string) => [place, what].filter(Boolean).join(' ');
const byNewest = (a: RecordRow, b: RecordRow) => (Date.parse(b.at ?? '') || 0) - (Date.parse(a.at ?? '') || 0);

export function buildPatientRecord(input: RecordInput): PatientRecordModel {
  const specialties = recordSpecialties(input.viewerSpecialty);
  const cards: Record<RecordSpecialty, Partial<Record<RecordCard, RecordRow[]>>> = { Dental: {}, Skin: {}, Hair: {} };
  const add = (specialty: RecordSpecialty, card: RecordCard, row: RecordRow) => {
    (cards[specialty][card] ??= []).push(row);
  };
  const row = (key: string, at: string | null | undefined, main: string, side: string, open?: RecordRow['open']): RecordRow =>
    ({ key, at: at ?? null, date: recordDate(at, input.today), main, side, ...(open ? { open } : {}) });
  const doctor = (id: number | null | undefined) => (id == null ? null : input.doctorName(id)) ?? (id == null ? 'Doctor not recorded' : `Doctor #${id}`);
  const historyBySheet = new Map(input.history.map(entry => [entry.caseSheetId, entry]));
  let unrecordedArea = false;
  const doneRecorded = new Set<string>();
  let activeCount = 0, acceptedCount = 0;

  for (const sheet of input.sheets) {
    const { specialty, recorded } = sheetSpecialty(sheet);
    if (!recorded) unrecordedArea = true;
    const visitAt = historyBySheet.get(sheet.id)?.createdAt ?? sheet.createdAt;
    const sheetDoctor = doctor(sheet.treatmentDoctorId);
    if (sheet.status === 'finalized') {
      add(specialty, 'Visits', row(`visit:${sheet.id}`, visitAt, sheet.chiefComplaint?.trim() || 'Reason not recorded', sheetDoctor));
    }
    for (const [index, finding] of (sheet.examination?.findings ?? []).entries()) {
      add(specialty, 'Findings', row(`finding:${sheet.id}:${index}`, visitAt, named(finding.toothCode, finding.label),
        [finding.noTreatmentNeeded ? 'No treatment needed' : finding.note.trim(), sheetDoctor].filter(Boolean).join(' · ')));
    }
    const work = input.todayWork[sheet.id];
    let facts: ReturnType<typeof latestTodayFacts> = [];
    try { facts = latestTodayFacts(work); } catch { facts = []; }
    for (const item of sheet.planItems) {
      const status = input.effectiveStatus?.(sheet.id, item.id) ?? item.status;
      if (status !== 'declined') { activeCount++; if (status === 'accepted') acceptedCount++; }
      const fact = facts.find(f => f.planItemId === item.id && f.owningCaseSheetId === sheet.id);
      const progress = item.completedAt ? `Completed ${recordDate(item.completedAt, input.today)}`
        : fact && fact.status === 'started' ? `Started ${recordDate(fact.visitDate, input.today)}` : null;
      add(specialty, 'Treatment plans', row(`plan:${sheet.id}:${item.id}`, visitAt, named(teeth(item), item.procedureName),
        [planItemStatusLabel(status), status === 'declined' ? item.declineReason?.trim() : null, progress].filter(Boolean).join(' · ')));
    }
    // Work recorded at this visit, on this or an earlier visit's treatment.
    for (const fact of facts) {
      if (fact.caseSheetId !== sheet.id || (fact.status !== 'started' && fact.status !== 'done')) continue;
      const owner = input.sheets.find(s => s.id === fact.owningCaseSheetId);
      const item = owner?.planItems.find(p => p.id === fact.planItemId);
      const what = item ? named(teeth(item), item.procedureName) : 'Recorded treatment';
      add(specialty, 'Treatments done', row(`work:${fact.id}`, fact.visitDate, `${what} – ${fact.status === 'done' ? 'completed' : 'started'}`,
        doctor(item?.performingDoctorId ?? null)));
      if (fact.status === 'done') doneRecorded.add(`${fact.owningCaseSheetId}:${fact.planItemId}`);
    }
    for (const [index, medicine] of (sheet.prescription?.medicines ?? []).entries()) {
      if (!medicine.name?.trim()) continue;
      add(specialty, 'Medicines', row(`medicine:${sheet.id}:${index}`, visitAt, [medicine.name.trim(), medicine.duration?.trim()].filter(Boolean).join(' · '), sheetDoctor));
    }
    for (const item of input.evidence[sheet.id] ?? []) {
      if (item.status !== 'active' || item.caseSheetId !== sheet.id) continue;
      const label = [evidenceClassLabel(item.evidenceClass), item.toothRefs?.join(', ')].filter(Boolean).join(' ');
      if (item.evidenceClass === 'xray' || item.evidenceClass === 'intraoral_photo') add(specialty, 'X-rays & reports', row(`evidence:${item.id}`, item.createdAt, item.remark?.trim() || label, 'Uploaded', { item }));
      else if (item.evidenceClass === 'case_sheet_page') add(specialty, 'Consents & documents', row(`evidence:${item.id}`, item.createdAt, 'Paper case sheet scan', 'Uploaded', { item }));
    }
    const wire = input.consents[sheet.id];
    if (wire?.available) {
      for (const consent of [...(wire.consultationConsents ?? []).filter(c => c.caseSheetId === sheet.id), ...wire.consents]) {
        if (consent.state === 'voided_superseded') continue;
        const names = consent.treatmentPlanItemIds.map(id => sheet.planItems.find(p => p.id === id)).filter((p): p is NonNullable<typeof p> => !!p)
          .map(p => named(teeth(p), p.procedureName.toLowerCase()));
        const main = consent.purpose === 'consultation' ? 'Consultation consent' : `Treatment consent${names.length ? ` – ${names.join(', ')}` : ''}`;
        const original = consent.signedEvidenceId ? (input.evidence[sheet.id] ?? []).find(e => e.id === consent.signedEvidenceId && e.status === 'active') : undefined;
        add(specialty, 'Consents & documents', row(`consent:${consent.id}`, consent.signedAt ?? consent.createdAt, main,
          consent.state === 'signed_digital' || consent.state === 'signed_physical_captured' ? 'Signed' : consentStateLabel(consent.state), original ? { item: original } : undefined));
      }
    }
  }
  // A completion saved without a today-work fact (earlier writer) is still work done.
  for (const sheet of input.sheets) for (const item of sheet.planItems) {
    if (!item.completedAt || doneRecorded.has(`${sheet.id}:${item.id}`)) continue;
    add(sheetSpecialty(sheet).specialty, 'Treatments done', row(`completed:${sheet.id}:${item.id}`, item.completedAt, `${named(teeth(item), item.procedureName)} – completed`, doctor(item.performingDoctorId ?? null)));
  }
  for (const appointment of input.appointments ?? []) {
    add('Dental', 'Appointments', row(`appointment:${appointment.appointmentId}`, appointment.scheduledAt,
      [appointment.clinicLabel, appointment.doctorName].filter(Boolean).join(' · ') || 'Appointment', appointmentStatusLabel(appointment.status)));
  }
  for (const specialty of Object.keys(cards) as RecordSpecialty[]) for (const list of Object.values(cards[specialty])) list?.sort(byNewest);
  const timeline = specialties.flatMap(specialty => RECORD_CARDS.flatMap(card => (cards[specialty][card] ?? []).map(r => ({ ...r, specialty, card }))))
    .sort(byNewest);
  const profile = input.profile;
  const medicine = profile?.medicationAllergies?.trim() || null;
  const anaesthesia = profile?.anaesthesiaAllergy === 'yes';
  const visitDates = input.history.map(e => e.createdAt).sort();
  const latestVisit = visitDates[visitDates.length - 1] ?? null;
  return {
    specialties, tabs: [...specialties, 'Timeline'], cards, timeline,
    unrecordedArea: unrecordedArea && specialties.length > 1,
    tiles: {
      allergies: { text: medicine || anaesthesia ? [medicine, anaesthesia ? 'Anaesthesia' : null].filter(Boolean).join(' · ')
        : profile?.anaesthesiaAllergy === 'no' && profile.lastConfirmedAt ? 'None recorded' : 'Not asked', alert: !!medicine || anaesthesia },
      activePlans: activeCount ? `${activeCount} · ${acceptedCount} accepted` : 'None yet',
      lastVisit: latestVisit ? recordDate(latestVisit, input.today) : '—',
      returnVisit: input.returnLine ?? 'Not set',
      historyConfirmed: profile?.lastConfirmedAt ? recordDate(profile.lastConfirmedAt, input.today) : 'Not yet',
    },
  };
}
